import type { HTMLAttributes } from 'react';
import { useState } from 'react';
import type { UseFormRegisterReturn } from 'react-hook-form';

import { cx, m } from './shared';

const OkTick = () => (
  <svg className={m('np-field__ok')} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
    <circle cx='12' cy='12' r='9' />
    <path d='m8.4 12.3 2.4 2.4 4.8-5' />
  </svg>
);

const EyeOn = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
    <path d='M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z' />
    <circle cx='12' cy='12' r='3' />
  </svg>
);

const EyeOff = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
    <path d='M3 3l18 18' />
    <path d='M10.6 6.1A9.8 9.8 0 0 1 12 6c6 0 9.5 6 9.5 6a16.6 16.6 0 0 1-3.2 3.7' />
    <path d='M6.6 6.7A16.4 16.4 0 0 0 2.5 12S6 18 12 18a9.6 9.6 0 0 0 4.2-.9' />
    <path d='M9.9 9.9a3 3 0 0 0 4.2 4.2' />
  </svg>
);

interface SignupFieldProps {
  id: string;
  label: string;
  placeholder: string;
  registration: UseFormRegisterReturn;
  error?: string;
  valid: boolean;
  autoComplete: string;
  inputMode?: HTMLAttributes<HTMLInputElement>['inputMode'];
  maxLength?: number;
  /** Password fields get the show/hide toggle; the labels say what the button will DO next. */
  password?: { show: string; hide: string };
  /** Persistent rule on the label line (the design's "mín. 8 caracteres"). */
  hint?: string;
}

/** One field of the design's signup card: label (+ valid tick) / control / message, in the card's own states. */
export function SignupField({ id, label, placeholder, registration, error, valid, autoComplete, inputMode, maxLength, password, hint }: SignupFieldProps) {
  const [visible, setVisible] = useState(false);
  const msgId = `${id}-msg`;
  const hintId = `${id}-hint`;
  const labelNode = (
    <label className={m('np-field__label')} htmlFor={id}>
      {label}
      <OkTick />
    </label>
  );

  return (
    <div className={m(cx('np-field', password && 'np-field--password', error ? 'np-field--invalid' : valid && 'np-field--valid'))}>
      {hint ? (
        <div className={m('np-field__top')}>
          {labelNode}
          <span className={m('np-field__hint')} id={hintId}>
            {hint}
          </span>
        </div>
      ) : (
        labelNode
      )}
      <div className={m('np-field__control')}>
        <input {...registration} className={m('np-field__input')} id={id} type={password && !visible ? 'password' : 'text'} maxLength={maxLength} autoComplete={autoComplete} dir={autoComplete === 'name' ? 'auto' : undefined} inputMode={inputMode} autoCapitalize={inputMode === 'email' ? 'off' : undefined} spellCheck={inputMode === 'email' ? false : undefined} placeholder={placeholder} aria-required='true' aria-invalid={error ? 'true' : undefined} aria-describedby={hint ? `${hintId} ${msgId}` : msgId} />
        {password && (
          <button className={m('np-field__toggle')} type='button' aria-label={visible ? password.hide : password.show} aria-controls={id} data-visible={visible ? 'true' : 'false'} onClick={() => setVisible((shown) => !shown)}>
            <span className={m('ico-on')}>
              <EyeOn />
            </span>
            <span className={m('ico-off')}>
              <EyeOff />
            </span>
          </button>
        )}
      </div>
      <span className={m(cx('np-field__msg', error && 'is-error'))} id={msgId}>
        {error ?? ''}
      </span>
    </div>
  );
}
